/* ==========================================================================
   Helpers
   ========================================================================== */

const hasClass = (el, className) => el.classList.contains(className);

const addClass = (el, className) => {
    className.split(' ').forEach(c => el.classList.add(c));
};

const removeClass = (el, className) => {
    className.split(' ').forEach(c => el.classList.remove(c));
};

const isVisible = el =>
    el.offsetWidth || el.offsetHeight || el.getClientRects().length;

/* ==========================================================================
   Constants
   ========================================================================== */

const FOCUSABLE =
    '[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable], audio[controls], video[controls], summary';

/* ==========================================================================
   Modal Class
   ========================================================================== */

class ModalInstance {
    constructor(element) {
        this.element = element;
        this.triggers = document.querySelectorAll(
            `[aria-controls="${this.element.getAttribute('id')}"]`
        );

        this.firstFocusable = null;
        this.lastFocusable = null;
        this.moveFocusEl = null;
        this.selectedTrigger = null;

        this.modalFocus = this.element.dataset.modalFirstFocus
            ? this.element.querySelector(this.element.dataset.modalFirstFocus)
            : null;

        this.preventScrollEl = this.getPreventScrollEl();
        this.showClass = 'Modal--is-visible';

        this.init();
    }

    getPreventScrollEl() {
        return this.element.dataset.modalPreventScroll
            ? document.querySelector(this.element.dataset.modalPreventScroll)
            : null;
    }

    init() {
        this.triggers.forEach(trigger => {
            trigger.addEventListener('click', e => {
                e.preventDefault();
                hasClass(this.element, this.showClass)
                    ? this.close()
                    : (this.selectedTrigger = e.currentTarget, this.open());
            });
        });

        this.element.addEventListener('openModal', e => {
            if (e.detail) this.selectedTrigger = e.detail;
            this.open();
        });

        this.element.addEventListener('closeModal', e => {
            if (e.detail) this.selectedTrigger = e.detail;
            this.close();
        });

        if (hasClass(this.element, this.showClass)) {
            this.bindEvents();
        }
    }

    open() {
        addClass(this.element, this.showClass);
        this.getFocusableElements();

        if (this.moveFocusEl) {
            this.moveFocusEl.focus();
            this.element.addEventListener(
                'transitionend',
                () => this.moveFocusEl.focus(),
                { once: true }
            );
        }

        this.emit('modalIsOpen');
        this.bindEvents();

        if (this.preventScrollEl) {
            this.preventScrollEl.style.overflow = 'hidden';
        }
    }

    close() {
        if (!hasClass(this.element, this.showClass)) return;

        removeClass(this.element, this.showClass);

        this.firstFocusable = null;
        this.lastFocusable = null;
        this.moveFocusEl = null;

        this.selectedTrigger?.focus();
        this.unbindEvents();
        this.emit('modalIsClose');

        if (this.preventScrollEl) {
            this.preventScrollEl.style.overflow = '';
        }
    }

    bindEvents() {
        this.element.addEventListener('keydown', this);
        this.element.addEventListener('click', this);
    }

    unbindEvents() {
        this.element.removeEventListener('keydown', this);
        this.element.removeEventListener('click', this);
    }

    handleEvent(e) {
        if (e.type === 'click') this.onClick(e);
        if (e.type === 'keydown') this.onKeydown(e);
    }

    onKeydown(e) {
        if (e.key === 'Tab') this.trapFocus(e);

        if (
            e.key === 'Enter' &&
            e.target.closest('.js-modal__close')
        ) {
            e.preventDefault();
            this.close();
        }
    }

    onClick(e) {
        if (
            e.target.closest('.js-modal__close') ||
            hasClass(e.target, 'js-modal')
        ) {
            e.preventDefault();
            this.close();
        }
    }

    trapFocus(e) {
        if (this.firstFocusable === document.activeElement && e.shiftKey) {
            e.preventDefault();
            this.lastFocusable.focus();
        }

        if (this.lastFocusable === document.activeElement && !e.shiftKey) {
            e.preventDefault();
            this.firstFocusable.focus();
        }
    }

    getFocusableElements() {
        const focusable = this.element.querySelectorAll(FOCUSABLE);

        this.firstFocusable = [...focusable].find(isVisible);
        this.lastFocusable = [...focusable].reverse().find(isVisible);

        this.moveFocusEl =
            this.modalFocus && this.modalFocus.matches(FOCUSABLE)
                ? this.modalFocus
                : this.firstFocusable;
    }

    emit(name) {
        this.element.dispatchEvent(
            new CustomEvent(name, { detail: this.selectedTrigger })
        );
    }
}

/* ==========================================================================
   Modal Video
   ========================================================================== */

class ModalVideo {
    constructor(modalEl) {
        this.modal = modalEl;
        this.media = modalEl.querySelector('.js-modal-video__media');
        this.isIframe = this.media.tagName.toLowerCase() === 'iframe';
        this.isOpen = false;

        this.init();
    }

    init() {
        this.addLoadListener();

        this.modal.addEventListener('modalIsOpen', e => {
            this.isOpen = true;
            this.media.src = e.detail
                .closest('[aria-controls]')
                .dataset.url;
        });

        this.modal.addEventListener('modalIsClose', () => {
            this.isOpen = false;
            this.modal.classList.add('modal--is-loading');
            this.media.src = '';
        });
    }

    addLoadListener() {
        const reveal = () => this.reveal();

        this.isIframe
            ? (this.media.onload = reveal)
            : this.media.addEventListener('loadedmetadata', reveal);
    }

    reveal() {
        if (!this.isOpen) return;

        this.modal.classList.remove('modal--is-loading');

        if (!this.modal.dataset.modalFirstFocus) {
            this.isIframe
                ? this.media.contentWindow.focus()
                : this.media.focus();
        }
    }
}

/* ==========================================================================
   Public API
   ========================================================================== */

const Modal = {
    init() {
        const modals = [...document.querySelectorAll('.js-modal')];

        if (!modals.length) return;

        const instances = modals.map(el => new ModalInstance(el));

        window.addEventListener('keydown', e => {
            if (e.key === 'Escape') {
                instances.forEach(modal => modal.close());
            }
        });

        document
            .querySelectorAll('.js-modal-video__media')
            .forEach(media =>
                new ModalVideo(media.closest('.js-modal'))
            );
    },
};

export default Modal;